import React from 'react';
import {
	Breakpoints,
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	Layout,
	Text,
	TextAlignment,
	TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import { DataCollectionCTAs } from '../components/data-collection-ctas';
import { CtaType } from '@esportal/portal-service';
import { PortalContext } from '../../../context/portal-context';
import { getUserName } from '../../../helpers/nameHelper';
import {
	DataCollectionStep,
	EndFormBackgroundContainer,
	formLayout,
	RelativeContainer,
	StyledIcon,
} from '../components/data-collection-form';
import { DescubreCarreterasIcon } from './forms-icons/descubre-carreteras-icon';
import { useTrackingManager } from '../../../tracking/use-tracking-manager';
import { formatOwnership } from '../../../tracking/tracking-helper';
import { FormDataCollection } from './types';

interface StepsState {
	previousStep: number;
	actualStep: number;
}

interface Form0Props {
	handleLinealNextStep: (answer?: string[]) => void;
	updateVisibleSteps: (newSteps: string[], isSelected: boolean) => void;
	getInitialSteps: (steps: DataCollectionStep[]) => void;
	steps: StepsState;
}

export const Form0: React.FC<Form0Props> = ({ handleLinealNextStep, updateVisibleSteps, getInitialSteps, steps }) => {
	const { portalService } = React.useContext(PortalContext);

	const formName = 'vw-club-data_collection-0';

	const userData = portalService.getPerson().getValue();
	const userName = getUserName(userData);

	const [answer, setAnswer] = React.useState<string[]>([]);
	const userVehicles = portalService.getPersonVehicles().getValue();
	const [isFormLoadTracked, setIsFormLoadTracked] = React.useState<boolean>(false);

	const trackingManager = useTrackingManager();

	React.useEffect(() => {
		if (userVehicles && steps.actualStep !== 0 && !isFormLoadTracked) {
			trackingManager.trackClubVWFormLoad(
				{
					FormName: formName,
					FormStart: true,
					FormTypeMultiple: ['otherform'],
				},
				{ contentId: formName },
				{ vehicle: formatOwnership(userVehicles?.vehicles) },
			);
			setIsFormLoadTracked(true);
		}
	}, [userVehicles, steps, isFormLoadTracked]);

	const handleAnswerChange = (value: string[]) => {
		setAnswer(value);
	};

	const handleLinealNextStepOnClick = (answer: string[], stepName: string) => {
		trackingManager.trackClubVWFormButtonClick(
			{
				FormName: formName,
				FormStart: false,
				FormTypeMultiple: ['otherform'],
			},
			{ contentId: `Listo - Answered step: ${stepName}` },
			{ vehicle: formatOwnership(userVehicles?.vehicles) },
		);
		handleLinealNextStep(answer);
		setAnswer([]);
	};

	const form0: FormDataCollection[] = [
		{
			name: 'INTRO_FORM',
			screen: (
				<Container wrap={'always'}>
					<Container
						padding={{
							bottom: ContainerPadding.static200,
						}}
					>
						<Text appearance={TokenTextAppearance.headline400}>
							¡Queremos <Text bold>adaptarnos a ti!</Text>
						</Text>
					</Container>
					<Container
						padding={{
							bottom: {
								[Breakpoints.default]: ContainerPadding.static500,
								[Breakpoints.b960]: ContainerPadding.static520,
							},
						}}
					>
						<Text appearance={TokenTextAppearance.bigcopy100}>
							Cada conductor, cada pasajero y cada viaje es único. Y tu experiencia con nosotros, también.
						</Text>
						<Text appearance={TokenTextAppearance.bigcopy100}>
							Cuanto más sepamos de ti, más personalizada será. Así que, ¿nos conocemos?
						</Text>
					</Container>
					<Container>
						<CTA
							tag="button"
							emphasis="primary"
							onClick={() => {
								handleLinealNextStep();
								trackingManager.trackClubVWButtonClick(
									'Empezar',
									{ contentId: `Start ${formName}` },
									{ vehicle: formatOwnership(userVehicles?.vehicles) },
								);
							}}
						>
							<Text>Empezar</Text>
						</CTA>
					</Container>
				</Container>
			),
			userLevel: 0,
			visibleStep: true,
		},
		{
			name: 'COMPARTE_COCHE',
			screen: (
				<Container wrap={'always'}>
					<Container
						padding={{
							bottom: ContainerPadding.static200,
						}}
					>
						<Text appearance={TokenTextAppearance.bigcopy200}>Empecemos por el principio...</Text>
					</Container>
					<Container>
						<Text appearance={TokenTextAppearance.headline400}>
							<Text bold>¿Compartes este Volkswagen </Text> con alguien?
						</Text>
					</Container>

					<Container
						horizontalAlign={'flex-start'}
						stretchContent={{ [Breakpoints.default]: true, [Breakpoints.b1280]: false }}
					>
						<DataCollectionCTAs
							type={CtaType.RADIO}
							ctaAnswers={[
								{ label: '😊 Sí, lo conduce alguien más', value: 'si' },
								{ label: '😎 No, es mi tesoro', value: 'no' },
							]}
							updateVisibleSteps={updateVisibleSteps}
							handleLinealNextStep={handleLinealNextStep}
							handleAnswerChange={handleAnswerChange}
							stepName="COMPARTE_COCHE"
							userLevel={0}
						/>
					</Container>
				</Container>
			),
			userLevel: 0,
			visibleStep: true,
		},
		{
			name: 'FAMILIA_CON_QUIEN',
			screen: (
				<Container wrap={{ [Breakpoints.default]: 'always' }}>
					<Container
						padding={{
							bottom: ContainerPadding.static100,
						}}
					>
						<Text appearance={TokenTextAppearance.bigcopy200}>Empecemos por el principio...</Text>
					</Container>
					<Container>
						<Text appearance={TokenTextAppearance.headline400}>
							Y quiénes suelen ser<Text bold> tus pasajeros?</Text>
						</Text>
					</Container>

					<DataCollectionCTAs
						type={CtaType.CHECKBOX}
						ctaAnswers={[
							{ label: '🌍 Viajo solo', value: 'solo' },
							{
								label: '❤️ Mi pareja',
								value: 'pareja',
								stepsToShow: [
									'FAMILIA_PAREJA_MUSICA',
									'FAMILIA_PAREJA_CONDUCE',
									'FAMILIA_PAREJA_GPS',
									'FAMILIA_PAREJA_SNACKS',
								],
							},
							{ label: '👨‍👩‍👧‍👦 Mi familia', value: 'familia', stepsToShow: ['FAMILIA_FAMILARES'] },
							{ label: '👯 Mis amigos', value: 'amigos', stepsToShow: ['FAMILIA_AMIGOS'] },
							{ label: '🐾 Mi amigo de 4 patas', value: 'mascota', stepsToShow: ['FAMILIA_MASCOTA'] },
							{ label: '🚗 Otros', value: 'otros' },
						]}
						updateVisibleSteps={updateVisibleSteps}
						handleLinealNextStep={handleLinealNextStep}
						handleAnswerChange={handleAnswerChange}
						stepName="FAMILIA_CON_QUIEN"
						userLevel={0}
					/>

					<Container
						padding={{
							top: {
								[Breakpoints.default]: ContainerPadding.static400,
								[Breakpoints.b960]: ContainerPadding.static520,
							},
							bottom: {
								[Breakpoints.default]: ContainerPadding.static500,
								[Breakpoints.b960]: ContainerPadding.static520,
							},
						}}
					>
						<CTA
							tag="button"
							emphasis="primary"
							disabled={answer.length == 0}
							onClick={() => {
								handleLinealNextStepOnClick(answer, 'FAMILIA_CON_QUIEN');
							}}
						>
							<Text>Listo</Text>
						</CTA>
					</Container>
				</Container>
			),
			userLevel: 0,
			visibleStep: true,
		},
		{
			name: 'FAMILIA_PAREJA_MUSICA',
			screen: (
				<Container wrap={'always'}>
					<Container
						padding={{
							bottom: {
								[Breakpoints.default]: ContainerPadding.static150,
								[Breakpoints.b960]: ContainerPadding.static200,
							},
						}}
					>
						<Text appearance={TokenTextAppearance.bigcopy100} bold>
							Vas muy bien acompañado en tu Volkswagen...
						</Text>
					</Container>
					<Container
						padding={{
							bottom: {
								[Breakpoints.default]: ContainerPadding.static500,
								[Breakpoints.b960]: ContainerPadding.static520,
							},
						}}
					>
						<Text appearance={TokenTextAppearance.headline300}>¿Nos cuentas más sobre tu pareja?</Text>
					</Container>

					<Container
						padding={{
							bottom: {
								[Breakpoints.default]: ContainerPadding.static150,
								[Breakpoints.b960]: ContainerPadding.static200,
							},
						}}
					>
						<Text appearance={TokenTextAppearance.bigcopy200}>Hagamos un quién es quién:</Text>
					</Container>
					<Container>
						<Text appearance={TokenTextAppearance.headline400} bold>
							¿Quién elige la música?
						</Text>
					</Container>
					<Container horizontalAlign={'flex-start'} stretchContent>
						<DataCollectionCTAs
							type={CtaType.RADIO}
							ctaAnswers={[
								{
									label: '❤️ Mi pareja',
									value: 'pareja',
								},
								{ label: '🖐 Yo', value: 'yo' },
							]}
							updateVisibleSteps={updateVisibleSteps}
							handleLinealNextStep={handleLinealNextStep}
							handleAnswerChange={handleAnswerChange}
							stepName="FAMILIA_PAREJA_MUSICA"
							userLevel={0}
						/>
					</Container>
				</Container>
			),
			userLevel: 0,
			visibleStep: false,
		},
		{
			name: 'FAMILIA_PAREJA_CONDUCE',
			screen: (
				<Container wrap={'always'}>
					<Container
						padding={{
							bottom: {
								[Breakpoints.default]: ContainerPadding.static150,
								[Breakpoints.b960]: ContainerPadding.static200,
							},
						}}
					>
						<Text appearance={TokenTextAppearance.bigcopy100} bold>
							Vas muy bien acompañado en tu Volkswagen...
						</Text>
					</Container>
					<Container
						padding={{
							bottom: {
								[Breakpoints.default]: ContainerPadding.static500,
								[Breakpoints.b960]: ContainerPadding.static520,
							},
						}}
					>
						<Text appearance={TokenTextAppearance.headline300}>¿Nos cuentas más sobre tu pareja?</Text>
					</Container>

					<Container
						padding={{
							bottom: {
								[Breakpoints.default]: ContainerPadding.static150,
								[Breakpoints.b960]: ContainerPadding.static200,
							},
						}}
					>
						<Text appearance={TokenTextAppearance.bigcopy200}>Hagamos un quién es quién:</Text>
					</Container>
					<Container>
						<Text bold appearance={TokenTextAppearance.headline400}>
							¿Quién conduce?
						</Text>
					</Container>
					<Container horizontalAlign={'flex-start'} stretchContent>
						<DataCollectionCTAs
							type={CtaType.RADIO}
							ctaAnswers={[
								{
									label: '❤️ Mi pareja',
									value: 'pareja',
								},
								{ label: '🖐 Yo', value: 'yo' },
							]}
							updateVisibleSteps={updateVisibleSteps}
							handleLinealNextStep={handleLinealNextStep}
							handleAnswerChange={handleAnswerChange}
							stepName="FAMILIA_PAREJA_CONDUCE"
							userLevel={0}
						/>
					</Container>
				</Container>
			),
			userLevel: 0,
			visibleStep: false,
		},
		{
			name: 'FAMILIA_PAREJA_GPS',
			screen: (
				<Container wrap={'always'}>
					<Container
						padding={{
							bottom: {
								[Breakpoints.default]: ContainerPadding.static150,
								[Breakpoints.b960]: ContainerPadding.static200,
							},
						}}
					>
						<Text appearance={TokenTextAppearance.bigcopy100} bold>
							Vas muy bien acompañado en tu Volkswagen...
						</Text>
					</Container>
					<Container
						padding={{
							bottom: {
								[Breakpoints.default]: ContainerPadding.static500,
								[Breakpoints.b960]: ContainerPadding.static520,
							},
						}}
					>
						<Text appearance={TokenTextAppearance.headline300}>¿Nos cuentas más sobre tu pareja?</Text>
					</Container>

					<Container
						padding={{
							bottom: {
								[Breakpoints.default]: ContainerPadding.static150,
								[Breakpoints.b960]: ContainerPadding.static200,
							},
						}}
					>
						<Text appearance={TokenTextAppearance.bigcopy200}>Hagamos un quién es quién:</Text>
					</Container>
					<Container>
						<Text appearance={TokenTextAppearance.headline400} bold>
							¿Quién se encarga del GPS?
						</Text>
					</Container>
					<Container horizontalAlign={'flex-start'} stretchContent>
						<DataCollectionCTAs
							type={CtaType.RADIO}
							ctaAnswers={[
								{
									label: '❤️ Mi pareja',
									value: 'pareja',
								},
								{ label: '🖐 Yo', value: 'yo' },
							]}
							updateVisibleSteps={updateVisibleSteps}
							handleLinealNextStep={handleLinealNextStep}
							handleAnswerChange={handleAnswerChange}
							stepName="FAMILIA_PAREJA_GPS"
							userLevel={0}
						/>
					</Container>
				</Container>
			),
			userLevel: 0,
			visibleStep: false,
		},
		{
			name: 'FAMILIA_PAREJA_SNACKS',
			screen: (
				<Container wrap={'always'}>
					<Container
						padding={{
							bottom: {
								[Breakpoints.default]: ContainerPadding.static100,
								[Breakpoints.b960]: ContainerPadding.static200,
							},
						}}
					>
						<Text appearance={TokenTextAppearance.bigcopy100} bold>
							Vas muy bien acompañado en tu Volkswagen...
						</Text>
					</Container>
					<Container
						padding={{
							bottom: {
								[Breakpoints.default]: ContainerPadding.static500,
								[Breakpoints.b960]: ContainerPadding.static520,
							},
						}}
					>
						<Text appearance={TokenTextAppearance.headline300}>¿Nos cuentas más sobre tu pareja?</Text>
					</Container>

					<Container
						padding={{
							bottom: {
								[Breakpoints.default]: ContainerPadding.static150,
								[Breakpoints.b960]: ContainerPadding.static200,
							},
						}}
					>
						<Text appearance={TokenTextAppearance.bigcopy200}>Hagamos un quién es quién:</Text>
					</Container>
					<Container>
						<Text appearance={TokenTextAppearance.headline400} bold>
							¿Quién trae los snacks?
						</Text>
					</Container>
					<Container horizontalAlign={'flex-start'} stretchContent>
						<DataCollectionCTAs
							type={CtaType.RADIO}
							ctaAnswers={[
								{
									label: '❤️ Mi pareja',
									value: 'pareja',
								},
								{ label: '🖐 Yo', value: 'yo' },
							]}
							updateVisibleSteps={updateVisibleSteps}
							handleLinealNextStep={handleLinealNextStep}
							handleAnswerChange={handleAnswerChange}
							stepName="FAMILIA_PAREJA_SNACKS"
							userLevel={0}
						/>
					</Container>
				</Container>
			),
			userLevel: 0,
			visibleStep: false,
		},
		{
			name: 'FAMILIA_FAMILARES',
			screen: (
				<Container wrap={'always'}>
					<Container
						padding={{
							bottom: {
								[Breakpoints.default]: ContainerPadding.static150,
								[Breakpoints.b960]: ContainerPadding.static200,
							},
						}}
					>
						<Text appearance={TokenTextAppearance.bigcopy200}>Empecemos por el principio...</Text>
					</Container>
					<Container>
						<Text appearance={TokenTextAppearance.headline400}>
							<Text bold>¿Con qué familiares</Text> sueles ir en coche?
						</Text>
					</Container>
					<Container horizontalAlign={'flex-start'} stretchContent>
						<DataCollectionCTAs
							type={CtaType.CHECKBOX}
							ctaAnswers={[
								{ label: '👶 Mis hijos', value: 'hijos', stepsToShow: ['FAMILIA_HIJOS'] },
								{ label: '👵👴 Mis abuelos', value: 'abuelos' },
								{ label: '👨‍👩‍👧 Mis padres', value: 'padres' },
								{ label: '👨‍👩‍👧‍👦 Otros familiares', value: 'otros' },
							]}
							updateVisibleSteps={updateVisibleSteps}
							handleLinealNextStep={handleLinealNextStep}
							handleAnswerChange={handleAnswerChange}
							stepName="FAMILIA_FAMILARES"
							userLevel={0}
						/>
					</Container>
					<Container
						padding={{
							top: {
								[Breakpoints.default]: ContainerPadding.static500,
								[Breakpoints.b960]: ContainerPadding.static520,
							},
							bottom: {
								[Breakpoints.default]: ContainerPadding.static500,
								[Breakpoints.b960]: ContainerPadding.static520,
							},
						}}
					>
						<CTA
							tag="button"
							emphasis="primary"
							disabled={answer.length == 0}
							onClick={() => {
								handleLinealNextStepOnClick(answer, 'FAMILIA_FAMILARES');
							}}
						>
							<Text>Listo</Text>
						</CTA>
					</Container>
				</Container>
			),
			userLevel: 0,
			visibleStep: false,
		},
		{
			name: 'FAMILIA_HIJOS',
			screen: (
				<Container wrap={'always'}>
					<Container
						padding={{
							bottom: {
								[Breakpoints.default]: ContainerPadding.static150,
								[Breakpoints.b960]: ContainerPadding.static200,
							},
						}}
					>
						<Text appearance={TokenTextAppearance.bigcopy100} bold>
							Vas muy bien acompañado en tu Volkswagen...
						</Text>
					</Container>

					<Container
						padding={{
							bottom: {
								[Breakpoints.default]: ContainerPadding.static500,
								[Breakpoints.b960]: ContainerPadding.static520,
							},
						}}
					>
						<Text appearance={TokenTextAppearance.headline300}>¿Nos cuentas más sobre tus hijos?</Text>
					</Container>

					<Container>
						<Text appearance={TokenTextAppearance.headline400} bold>
							¿Cuántos peques son?
						</Text>
					</Container>
					<Container
						padding={{
							right: {
								[Breakpoints.b960]: ContainerPadding.grid008,
								[Breakpoints.b1280]: ContainerPadding.grid011,
								[Breakpoints.b1600]: ContainerPadding.grid009,
								[Breakpoints.b1920]: ContainerPadding.grid008,
								[Breakpoints.b2560]: ContainerPadding.grid006,
							},
						}}
						stretchContent
					>
						<DataCollectionCTAs
							type={CtaType.SELECT}
							ctaAnswers={[
								{ label: '👶 Selecciona el número', value: '' },
								{ label: '👶 Uno', value: '1' },
								{ label: '👶 Dos', value: '2' },
								{ label: '👶 Tres', value: '3' },
								{ label: '👶 Cuatro', value: '4' },
								{ label: '👶 Cinco', value: '5' },
								{ label: '👶 Seis', value: '6' },
								{ label: '👶 Siete', value: '7' },
								{ label: '👶 Ocho', value: '8' },
								{ label: '👶 Nueve', value: '9' },
							]}
							updateVisibleSteps={updateVisibleSteps}
							handleLinealNextStep={handleLinealNextStep}
							handleAnswerChange={handleAnswerChange}
							stepName="FAMILIA_HIJOS"
							userLevel={0}
							isLabelFloating={true}
							selectLabel="👶 Selecciona el número"
						/>
					</Container>
					<Container
						padding={{
							bottom: {
								[Breakpoints.default]: ContainerPadding.static500,
								[Breakpoints.b960]: ContainerPadding.static520,
							},
						}}
					>
						<CTA
							tag="button"
							emphasis="primary"
							disabled={answer.length == 0}
							onClick={() => {
								handleLinealNextStepOnClick(answer, 'FAMILIA_HIJOS');
							}}
						>
							<Text>Listo</Text>
						</CTA>
					</Container>
				</Container>
			),
			userLevel: 0,
			visibleStep: false,
		},
		{
			name: 'FAMILIA_AMIGOS',
			screen: (
				<Container wrap={'always'} stretchContent>
					<Container
						padding={{
							bottom: {
								[Breakpoints.default]: ContainerPadding.static150,
								[Breakpoints.b960]: ContainerPadding.static200,
							},
						}}
					>
						<Text appearance={TokenTextAppearance.bigcopy100} bold>
							Vas muy bien acompañado en tu Volkswagen...
						</Text>
					</Container>

					<Container
						padding={{
							bottom: {
								[Breakpoints.default]: ContainerPadding.static500,
								[Breakpoints.b960]: ContainerPadding.static520,
							},
						}}
					>
						<Text appearance={TokenTextAppearance.headline300}>¿Nos cuentas más sobre tus amigos?</Text>
					</Container>

					<Container>
						<Text appearance={TokenTextAppearance.headline400}>
							Tú y tus amigos, <Text bold>¿tenéis nombre de grupo?</Text>
						</Text>
					</Container>
					<Container
						padding={{
							right: {
								[Breakpoints.b960]: ContainerPadding.grid007,
								[Breakpoints.b1280]: ContainerPadding.grid010,
								[Breakpoints.b1600]: ContainerPadding.grid008,
								[Breakpoints.b2560]: ContainerPadding.grid006,
							},
						}}
						stretchContent
					>
						<DataCollectionCTAs
							type={CtaType.INPUT}
							ctaAnswers={[{ label: '🤘 Escríbelo aquí', value: 'opcion1' }]}
							updateVisibleSteps={updateVisibleSteps}
							handleLinealNextStep={handleLinealNextStep}
							handleAnswerChange={handleAnswerChange}
							stepName="FAMILIA_AMIGOS"
							userLevel={0}
						/>
					</Container>
					<Container
						padding={{
							top: {
								[Breakpoints.default]: ContainerPadding.static500,
								[Breakpoints.b960]: ContainerPadding.static520,
							},
							bottom: {
								[Breakpoints.default]: ContainerPadding.static500,
								[Breakpoints.b960]: ContainerPadding.static520,
							},
						}}
						stretchContent
					>
						<CTA
							tag="button"
							emphasis="primary"
							disabled={answer.length == 0}
							onClick={() => {
								handleLinealNextStepOnClick(answer, 'FAMILIA_AMIGOS');
							}}
						>
							<Text>Listo</Text>
						</CTA>
					</Container>
				</Container>
			),
			userLevel: 0,
			visibleStep: false,
		},
		{
			name: 'FAMILIA_MASCOTA',
			screen: (
				<>
					<Container
						padding={{
							bottom: {
								[Breakpoints.default]: ContainerPadding.static150,
								[Breakpoints.b960]: ContainerPadding.static200,
							},
						}}
					>
						<Text appearance={TokenTextAppearance.bigcopy100} bold>
							Vas muy bien acompañado en tu Volkswagen...
						</Text>
					</Container>

					<Container
						padding={{
							bottom: {
								[Breakpoints.default]: ContainerPadding.static500,
								[Breakpoints.b960]: ContainerPadding.static520,
							},
						}}
					>
						<Text appearance={TokenTextAppearance.headline300}>¿Nos cuentas más sobre tus amigo de 4 patas?</Text>
					</Container>

					<Container>
						<Text appearance={TokenTextAppearance.headline400} bold>
							¿Qué mascota tienes?
						</Text>
					</Container>
					<DataCollectionCTAs
						type={CtaType.CHECKBOX}
						ctaAnswers={[
							{ label: '🐶 Perro/s', value: 'perro' },
							{ label: '🐱 Gato/s', value: 'gato' },
							{ label: '🐰 Conejo/s', value: 'conejo' },
							{ label: '🐾 Otro', value: 'otro' },
						]}
						updateVisibleSteps={updateVisibleSteps}
						handleLinealNextStep={handleLinealNextStep}
						handleAnswerChange={handleAnswerChange}
						stepName="FAMILIA_MASCOTA"
						userLevel={0}
					/>
					<Container
						padding={{
							top: {
								[Breakpoints.default]: ContainerPadding.static500,
								[Breakpoints.b960]: ContainerPadding.static520,
							},
							bottom: {
								[Breakpoints.default]: ContainerPadding.static500,
								[Breakpoints.b960]: ContainerPadding.static520,
							},
						}}
					>
						<CTA
							tag="button"
							emphasis="primary"
							disabled={answer.length == 0}
							onClick={() => {
								handleLinealNextStepOnClick(answer, 'FAMILIA_MASCOTA');
							}}
						>
							<Text>Listo</Text>
						</CTA>
					</Container>
				</>
			),
			userLevel: 0,
			visibleStep: false,
		},
		{
			name: 'END_FORM',
			screen: (
				<EndFormBackgroundContainer className="end-form-0__background">
					<Layout data-testid="end-form__layout" appearance={formLayout} allowOverflowingContent>
						<RelativeContainer className="end-form__relative-container">
							<Container
								verticalAlign={'center'}
								horizontalAlign={'center'}
								wrap={{ [Breakpoints.default]: 'always', [Breakpoints.b960]: 'never' }}
								gutter={{ [Breakpoints.b960]: ContainerGutter.grid001 }}
								shrinkContent
							>
								<Container
									horizontalAlign={'center'}
									verticalAlign={'center'}
									padding={{
										bottom: {
											[Breakpoints.default]: ContainerPadding.static200,
											[Breakpoints.b960]: ContainerPadding.static0,
										},
									}}
								>
									<StyledIcon className="icon-container">
										<DescubreCarreterasIcon />
									</StyledIcon>
								</Container>
								<Container
									horizontalAlign={{ [Breakpoints.default]: 'center', [Breakpoints.b960]: 'flex-start' }}
									verticalAlign={'center'}
									wrap={'always'}
								>
									<Container
										horizontalAlign={{ [Breakpoints.default]: 'center', [Breakpoints.b960]: 'flex-start' }}
										verticalAlign={'center'}
										padding={{
											bottom: ContainerPadding.static200,
										}}
										wrap={'always'}
										shrinkContent
									>
										<Text
											appearance={TokenTextAppearance.headline400}
											textAlign={{
												[Breakpoints.default]: TextAlignment.center,
												[Breakpoints.b960]: TextAlignment.left,
											}}
											bold
										>
											¡Ya eres Descubre carreteras!
										</Text>
									</Container>
									<Container
										horizontalAlign={{ [Breakpoints.default]: 'center', [Breakpoints.b960]: 'flex-start' }}
										verticalAlign={'center'}
										wrap={'always'}
										padding={{
											bottom: {
												[Breakpoints.default]: ContainerPadding.static200,
											},
										}}
									>
										<Text
											appearance={TokenTextAppearance.bigcopy100}
											textAlign={{
												[Breakpoints.default]: TextAlignment.center,
												[Breakpoints.b960]: TextAlignment.left,
											}}
										>
											Nos encanta conocerte, {userName}. Nos ayuda mucho para contarte solo cosas que te interesen en el
											futuro.
										</Text>
									</Container>
									<Container
										horizontalAlign={{ [Breakpoints.default]: 'center', [Breakpoints.b960]: 'flex-start' }}
										verticalAlign={'center'}
										wrap={'always'}
										padding={{
											bottom: {
												[Breakpoints.default]: ContainerPadding.static500,
												[Breakpoints.b960]: ContainerPadding.static520,
											},
										}}
									>
										<Text
											appearance={TokenTextAppearance.headline200}
											textAlign={{
												[Breakpoints.default]: TextAlignment.center,
												[Breakpoints.b960]: TextAlignment.left,
											}}
											bold
										>
											¿Quieres seguir respondiendo y subir de nivel?
										</Text>
									</Container>
									<Text
										textAlign={{
											[Breakpoints.default]: TextAlignment.center,
											[Breakpoints.b960]: TextAlignment.left,
										}}
									>
										<CTA
											tag="button"
											emphasis="primary"
											onClick={() => {
												handleLinealNextStep();
												trackingManager.trackClubVWButtonClick(
													'A por el siguiente nivel',
													{
														contentId: 'Form-0 completed',
													},
													{ vehicle: formatOwnership(userVehicles?.vehicles) },
												);
											}}
										>
											A por el siguiente nivel
										</CTA>
									</Text>
								</Container>
							</Container>
						</RelativeContainer>
					</Layout>
				</EndFormBackgroundContainer>
			),
			userLevel: 0,
			visibleStep: true,
		},
	];

	React.useEffect(() => {
		const findDataCollectionCTAs = (children: any) => {
			let ctaAnswers: any = [];

			React.Children.forEach(children, (child) => {
				if (!child) return;

				if (child.type === DataCollectionCTAs) {
					ctaAnswers.push(...(child.props.ctaAnswers || []));
				}

				if (child.props && child.props.children) {
					ctaAnswers = ctaAnswers.concat(findDataCollectionCTAs(child.props.children));
				}
			});

			return ctaAnswers;
		};

		const stepNamesAndVisibility = form0.map((step) => {
			const ctaAnswers = findDataCollectionCTAs(step.screen.props.children);

			return {
				name: step.name,
				visibleStep: step.visibleStep,
				questionLevel: step.userLevel,
				ctaAnswers: ctaAnswers.length > 0 ? ctaAnswers : null,
			};
		});

		getInitialSteps(stepNamesAndVisibility);
		setAnswer([]);
	}, []);

	const currentFormScreen = form0[steps.actualStep].screen;

	return <>{currentFormScreen}</>;
};
